import { useEffect } from 'react';
import { useBooleanFlagValue } from '@worktango/feature-flags-react';
import { companySelectors } from '~/behaviors/selectors/company.selectors';
import { accountSelectors } from '~/behaviors/selectors/account.selectors';
import { permissionSelectors } from '~/behaviors/selectors/permission.selectors';
import { useAppSelector } from '~/behaviors/redux';
import type { AccountReducer } from '~/behaviors/types/account.types';
import { PermissionsReducer } from '~/behaviors/types/permissions.types';
import type { UserPermissions } from '~/interfaces/permission.interface';
import type { Company } from '~/interfaces/company.interface';
import { getAccessToken } from '~/adapters/tokens';
import { surveyUrl, kazooLogoutEndpoint } from '~/config/settings/worktango.settings';
import { jss } from '~/tools/jss';

// These must match the constants exported from apps/pulse/survey/src/hooks/usePreviewToken.js
const PREVIEW_TOKEN_REQUEST = 'WORKTANGO_REQUEST_PREVIEW_TOKEN';
const PREVIEW_TOKEN_RESPONSE = 'WORKTANGO_PREVIEW_AUTH_TOKEN';
import { urls } from '~/tools/urls';
import MainRoutes from '../Main.routes';
import Header from './Header';
import Sidebar from './Sidebar';
import { ScrollToTop } from '~/views/UI.shared';

const useStyles = jss.makeStyles((theme) => {
  const sidebarWidth = '248px';

  return {
    main: {
      display: 'block',
      position: 'relative',
      minHeight: '100vh',
    },
    header: {
      zIndex: 1000,
      position: 'fixed',
      top: 0,
      right: 0,
      left: sidebarWidth,
      height: theme.layout.headerHeight,
      boxShadow: '0 -10px 20px 10px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden',
    },
    page: {
      zIndex: 1,
      display: 'block',
      position: 'absolute',
      height: '100%',
      top: 0,
      left: sidebarWidth,
      right: 0,
      bottom: 0,
      margin: 0,
      padding: `${theme.layout.headerHeight}px 0 0 0`,
      backgroundColor: theme.palette.gray6,
    },
    sidebar: {
      zIndex: 1001,
      position: 'fixed',
      overflow: 'hidden',
      top: 0,
      bottom: 0,
      left: 0,
      width: sidebarWidth,
      display: 'flex',
      flexDirection: 'column',
    },
  };
});

const MainLayout = () => {
  const styles = useStyles();

  useEffect(() => {
    const surveyOrigin = new URL(surveyUrl).origin;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== surveyOrigin) return;
      if (event.data?.type !== PREVIEW_TOKEN_REQUEST) return;
      const token = getAccessToken();
      if (token) {
        (event.source as Window).postMessage({ type: PREVIEW_TOKEN_RESPONSE, token }, surveyOrigin);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const { name, avatarUrl, companyName, companyLogoUrl, companyColor, isAdmin, isImpersonating } = useAppSelector(
    (state) => state.account,
  );
  const {
    canSeeSurveys,
    canSeeConversations,
    canSeeSettings,
    canSeeEmployees,
    canSeeUsers,
    canSeeActionPlanning,
    canManageMailingList,
  } = useAppSelector((state) => state.permissions);

  const account = useAppSelector<AccountReducer>(accountSelectors.selectAccount());
  const enableSurveyLifeCycle = useBooleanFlagValue('continuous-surveys', false);

  const permissions = useAppSelector<PermissionsReducer>(permissionSelectors.selectPermissions());

  const onlyKWCustomRole =
    permissions.hasKWCustomRole &&
    Object.keys(permissions.surveyTypes).length === 0 &&
    permissions.globalActionNames.length === 0;

  const canSeeLearningCoach = !onlyKWCustomRole;

  const company = useAppSelector<Company>(companySelectors.selectCompany(account.companyId));
  const canSeeKazoo = !!company.workspace;

  const kazooLogoutUrl = `${urls.createKazooBaseUrl(company.workspace ?? '')}${kazooLogoutEndpoint}`;
  const kwPlatformUrl = `${urls.createKazooBaseUrl(company.workspace ?? '')}`;

  return (
    <main className={styles.main}>
      <ScrollToTop />
      <header className={styles.header}>
        <Header
          name={name}
          color={companyColor}
          avatarUrl={avatarUrl}
          companyName={companyName}
          companyLogoUrl={companyLogoUrl}
          canSeeSettings={canSeeSettings}
          isImpersonating={isImpersonating}
        />
      </header>

      <div className={styles.page}>
        <MainRoutes />
      </div>

      <aside className={styles.sidebar}>
        <Sidebar
          showLeaveSurveysAndInsights={canSeeKazoo}
          showSurveys={canSeeSurveys}
          showTrends={canSeeSurveys}
          showConversations={canSeeConversations}
          showActionPlanning={canSeeActionPlanning && !onlyKWCustomRole}
          showEmployees={canSeeEmployees}
          showUsers={canSeeUsers}
          showMailingLists={canManageMailingList && enableSurveyLifeCycle}
          showSettings={canSeeSettings}
          showAdmin={isAdmin}
          showLearningCoach={canSeeLearningCoach}
          kazooPlatformUrl={kwPlatformUrl}
          kazooLogoutUrl={kazooLogoutUrl}
        />
      </aside>
    </main>
  );
};

export default MainLayout;
